import { List, Record, Set, OrderedSet, fromJS } from 'immutable';

import {
  dataAdapter,
  includesAdapter,
  JournalFactory,
  MetaFactory,
  matchCell,
  jeGridLineColumnsAdapter,
} from '@/domain/journal/helper';
import { parseMatchedLines } from '@/domain/journal/schemas';

export const messageFactory = new Record({
  level: 'info',
  text: '',
  type: '',
});

const rowMessageFactory = new Record({
  row: 0,
  message: '',
  type: 'info',
});

// eslint-disable-next-line max-len
export function messagesAdapter(messages) {
  return messages.reduce((a, msg) => a.push(messageFactory(msg)), new List());
}

// eslint-disable-next-line max-len
export function rowMessagesAdapter(messages) {
  return messages.reduce((a, msg) => a.push(rowMessageFactory(msg)), new List());
}

export function JEAdapter(rawData) {
  const {
    entries,
    includes,
    line_items, // eslint-disable-line camelcase
    header_lines_number, // eslint-disable-line camelcase
    header_settings, // eslint-disable-line camelcase
    lines,
    summary,
    row_messages, // eslint-disable-line camelcase
    initial_hidden_columns, // eslint-disable-line camelcase
    messages,
    publicationWarnings,
    currentPage,
    pageCount,
    reconcile_column, // eslint-disable-line camelcase
    pinned_column, // eslint-disable-line camelcase
    amount_column, // eslint-disable-line camelcase
    balance_column, // eslint-disable-line camelcase
    txn_id_column, // eslint-disable-line camelcase
    transaction_type_column, // eslint-disable-line camelcase
    date_column, // eslint-disable-line camelcase
    description_column, // eslint-disable-line camelcase
    not_hidden_columns, // eslint-disable-line camelcase
    error_from_erp, // eslint-disable-line camelcase
    two_way_match, // eslint-disable-line camelcase
    matchedLines,
    ...data
  } = rawData;

  return JournalFactory({
    ...data,
    matchedLines: fromJS(parseMatchedLines(matchedLines)),
    messages: messagesAdapter(messages),
    publicationWarnings: Set(publicationWarnings),
    entry: dataAdapter(entries),
    includes: includesAdapter(includes),
    line_items: OrderedSet(line_items || []), // eslint-disable-line camelcase
    settings: Set(header_settings || []), // eslint-disable-line camelcase
    lines: OrderedSet(lines || []), // eslint-disable-line camelcase
    headerLinesNumber: header_lines_number, // eslint-disable-line camelcase
    // eslint-disable-next-line max-len
    rowMessages: rowMessagesAdapter(row_messages || []), // eslint-disable-line camelcase
    summary: Set(summary || []), // eslint-disable-line camelcase
    initial_hidden_columns: List(initial_hidden_columns || []), // eslint-disable-line camelcase
    reconcileColumn: reconcile_column, // eslint-disable-line camelcase
    pinnedColumn: List(pinned_column || []), // eslint-disable-line camelcase
    amountColumn: amount_column, // eslint-disable-line camelcase
    balanceColumn: balance_column, // eslint-disable-line camelcase
    transactionIdColumn: txn_id_column, // eslint-disable-line camelcase
    dateColumn: date_column, // eslint-disable-line camelcase
    descriptionColumn: description_column, // eslint-disable-line camelcase
    transactionTypeColumn: transaction_type_column, // eslint-disable-line camelcase
    errorFromErp: error_from_erp, // eslint-disable-line camelcase
    notHiddenColumns: Set(not_hidden_columns || []), // eslint-disable-line camelcase
    meta: MetaFactory({
      currentPage,
      pageCount,
    }),
    jeGridLineColumnsDictionary: jeGridLineColumnsAdapter(entries),
    textractForVendorEnabled: two_way_match,
  });
}

export function itemAdapter(item, selectedRows = new Set(), value) {
  const { _cell, _options, _row, _col, cellSet, valid, ...rest } = item.toJS();
  const [colName, rowNumber] = matchCell(_cell);
  const rowNumbersUpdate = selectedRows.has(rowNumber) ? selectedRows : Set([rowNumber]);

  const data =
    typeof cellSet !== 'undefined'
      ? {
          ...rest,
          set: {
            ...cellSet,
            value: typeof value === 'undefined' ? cellSet.value : value,
          },
        }
      : rest;

  return rowNumbersUpdate.reduce((res, row) => {
    const cellName = `${colName}${row}`;
    return { ...res, [cellName]: data };
  }, {});
}
